const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Euler vs RK4 — Integration Error Visualised ───────
function DiagramEulerRK4() {
  const W = 900, H = 360;
  const PW = (W - 30) / 2;

  // True solution: y' = -y + sin(t),  y(0)=0  →  y(t) = 0.5*(sin t - cos t) + 0.5*e^(-t)
  const trueSol = t => 0.5 * (Math.sin(t) - Math.cos(t)) + 0.5 * Math.exp(-t);
  const f = (t, y) => -y + Math.sin(t);

  const tEnd = 8, nTrue = 400;
  const tTrue = Array.from({ length: nTrue }, (_, i) => i / (nTrue - 1) * tEnd);
  const yTrue = tTrue.map(t => trueSol(t));

  // Euler with dt = 0.5 (coarse)
  const dtE = 0.5;
  const eulerPts = [{ t: 0, y: 0 }];
  let yE = 0;
  for (let ti = dtE; ti <= tEnd + 0.01; ti += dtE) {
    const prev = eulerPts[eulerPts.length - 1];
    yE = prev.y + dtE * f(prev.t, prev.y);
    eulerPts.push({ t: ti, y: yE });
  }

  // RK4 with dt = 0.5 (same step)
  const dtRK = 0.5;
  const rk4Pts = [{ t: 0, y: 0 }];
  let yRK = 0;
  for (let ti = dtRK; ti <= tEnd + 0.01; ti += dtRK) {
    const prev = rk4Pts[rk4Pts.length - 1];
    const k1 = f(prev.t, prev.y);
    const k2 = f(prev.t + dtRK / 2, prev.y + dtRK / 2 * k1);
    const k3 = f(prev.t + dtRK / 2, prev.y + dtRK / 2 * k2);
    const k4 = f(prev.t + dtRK, prev.y + dtRK * k3);
    yRK = prev.y + dtRK / 6 * (k1 + 2 * k2 + 2 * k3 + k4);
    rk4Pts.push({ t: ti, y: yRK });
  }

  const ox = 14, oy = 230, ow = PW - 28, oh = 170;
  const toX = t => ox + (t / tEnd) * ow;
  const toY = y => oy - Math.max(-1.2, Math.min(1.2, y)) / 1.2 * oh * 0.82;

  // Error plot
  const eulerErr = eulerPts.map(p => ({ t: p.t, e: Math.abs(p.y - trueSol(p.t)) }));
  const rk4Err   = rk4Pts.map(p => ({ t: p.t, e: Math.abs(p.y - trueSol(p.t)) }));
  const maxErr = Math.max(...eulerErr.map(p => p.e));
  const ox2 = PW + 36, oy2 = 260, ow2 = PW - 50, oh2 = 200;
  const toX2 = t => ox2 + (t / tEnd) * ow2;
  const toYe = e => oy2 - Math.min(e / (maxErr * 1.1), 1) * oh2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Euler vs. RK4 Integration — Accuracy Comparison (dt = 0.5, y&apos; = −y + sin t)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="e1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Panel 1: Solutions */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Numerical Solution vs. Exact — Same Step dt = 0.5</text>

        {/* Grid */}
        {[-1, -0.5, 0, 0.5, 1].map(v => {
          const y = toY(v);
          return (
            <g key={v}>
              <line x1={ox} y1={y} x2={ox + ow} y2={y} stroke={v === 0 ? '#e8e8e8' : '#f5f5f5'} strokeWidth="1" />
              <text x={ox - 4} y={y + 4} textAnchor="end" fontSize="9" fill="#aaa">{v}</text>
            </g>
          );
        })}
        {[0, 2, 4, 6, 8].map(t => (
          <text key={t} x={toX(t)} y={oy + 14} textAnchor="middle" fontSize="9" fill="#aaa">{t}</text>
        ))}

        {/* True solution */}
        <polyline points={tTrue.map((t, i) => `${toX(t)},${toY(yTrue[i])}`).join(' ')}
          fill="none" stroke="#555" strokeWidth="2" />

        {/* Euler */}
        <polyline points={eulerPts.map(p => `${toX(p.t)},${toY(p.y)}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2" strokeDasharray="5,3" />
        {eulerPts.map((p, i) => (
          <circle key={i} cx={toX(p.t)} cy={toY(p.y)} r={3} fill="#c0392b" />
        ))}

        {/* RK4 */}
        <polyline points={rk4Pts.map(p => `${toX(p.t)},${toY(p.y)}`).join(' ')}
          fill="none" stroke="#2E75B6" strokeWidth="2" />
        {rk4Pts.map((p, i) => (
          <circle key={i} cx={toX(p.t)} cy={toY(p.y)} r={3} fill="#2E75B6" />
        ))}

        {/* Axes */}
        <line x1={ox} y1={oy} x2={ox + ow + 8} y2={oy} stroke="#888" strokeWidth="1.5" markerEnd="url(#e1)" />
        <line x1={ox} y1={oy + oh * 0.82 + 4} x2={ox} y2={toY(1.2) - 6} stroke="#888" strokeWidth="1.5" markerEnd="url(#e1)" />
        <text x={ox + ow + 12} y={oy + 4} fontSize="10" fill="#555">t</text>
        <text x={ox + 4} y={toY(1.2) - 8} fontSize="10" fill="#555">y</text>

        {/* Legend */}
        <line x1={ox + 8} y1={H - 54} x2={ox + 26} y2={H - 54} stroke="#555" strokeWidth="2" />
        <text x={ox + 30} y={H - 50} fontSize="9.5" fill="#555">Exact solution</text>
        <line x1={ox + 8} y1={H - 38} x2={ox + 26} y2={H - 38} stroke="#c0392b" strokeWidth="2" strokeDasharray="5,3" />
        <text x={ox + 30} y={H - 34} fontSize="9.5" fill="#c0392b">Euler (O(dt) error)</text>
        <line x1={ox + 8} y1={H - 22} x2={ox + 26} y2={H - 22} stroke="#2E75B6" strokeWidth="2" />
        <text x={ox + 30} y={H - 18} fontSize="9.5" fill="#2E75B6">RK4  (O(dt⁴) error)</text>

        {/* Panel 2: Error plot */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Global Error |y_numerical − y_exact| vs. Time</text>

        {/* Grid error */}
        {[0, 0.1, 0.2, 0.3, 0.4].map(v => {
          const y = toYe(v * maxErr);
          if (y < 10 || y > oy2 + 4) return null;
          return (
            <g key={v}>
              <line x1={ox2} y1={y} x2={ox2 + ow2} y2={y} stroke="#f5f5f5" strokeWidth="1" />
              <text x={ox2 - 4} y={y + 4} textAnchor="end" fontSize="8.5" fill="#aaa">{(v * maxErr).toFixed(2)}</text>
            </g>
          );
        })}

        {/* Euler error */}
        <polyline points={eulerErr.map(p => `${toX2(p.t)},${toYe(p.e)}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="2.2" />

        {/* RK4 error */}
        <polyline points={rk4Err.map(p => `${toX2(p.t)},${toYe(p.e)}`).join(' ')}
          fill="none" stroke="#2E75B6" strokeWidth="2.2" />

        {/* Axes error */}
        <line x1={ox2} y1={oy2 + 4} x2={ox2 + ow2 + 8} y2={oy2 + 4} stroke="#888" strokeWidth="1.5" markerEnd="url(#e1)" />
        <line x1={ox2} y1={oy2 + 4} x2={ox2} y2={oy2 - oh2 - 8} stroke="#888" strokeWidth="1.5" markerEnd="url(#e1)" />
        <text x={ox2 + ow2 / 2} y={oy2 + 18} textAnchor="middle" fontSize="10" fill="#555">t</text>
        <text x={ox2 + 4} y={oy2 - oh2 - 10} fontSize="10" fill="#555">|e|</text>
        {[0, 2, 4, 6, 8].map(t => (
          <text key={t} x={toX2(t)} y={oy2 + 15} textAnchor="middle" fontSize="9" fill="#aaa">{t}</text>
        ))}

        {/* Euler much larger label */}
        {(() => {
          const p = eulerErr[eulerErr.length - 2];
          return <text x={toX2(p.t) - 4} y={toYe(p.e) - 6} textAnchor="end" fontSize="9.5" fill="#c0392b" fontWeight="700">Euler error</text>;
        })()}
        {(() => {
          const p = rk4Err[rk4Err.length - 2];
          return <text x={toX2(p.t) - 4} y={toYe(p.e) + 14} textAnchor="end" fontSize="9.5" fill="#2E75B6" fontWeight="700">RK4 error (tiny)</text>;
        })()}

        {/* Order box */}
        <rect x={ox2 + 4} y={oy2 - oh2 + 10} width={ow2 - 8} height={38} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
        <text x={ox2 + 10} y={oy2 - oh2 + 24} fontSize="10" fill="#7B0D0D" fontWeight="700">Error order: Euler O(dt),  RK4 O(dt⁴)</text>
        <text x={ox2 + 10} y={oy2 - oh2 + 38} fontSize="9.5" fill="#333">Halving dt: Euler error halves;  RK4 error shrinks 16×</text>
      </svg>
      <div style={S.dCaption}>For the same step size dt = 0.5, Euler (1st-order) accumulates significant global error that grows with time, while RK4 (4th-order) remains virtually indistinguishable from the exact solution. Euler is first-order accurate: global error scales as O(dt). RK4 is fourth-order: global error scales as O(dt⁴). Halving dt therefore reduces Euler error by a factor of 2 but reduces RK4 error by a factor of 16 — explaining why RK4 is the default integrator for most ODE solvers in system dynamics.</div>
    </div>
  );
}

// ── Diagram 2: Runge-Kutta Family — Stage Diagram ─────────────────
function DiagramRKFamily() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Runge-Kutta Integration Family — Stage Evaluations and Butcher Tableaux</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="rk1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
        </defs>

        {/* Timeline showing stage evaluations */}
        {/* RK1 (Euler) */}
        {[
          { label: 'RK1 (Euler)', col: '#c0392b', stages: [0], weights: [1], y: 42, order: 1 },
          { label: 'RK2 (Midpoint)', col: '#e07b00', stages: [0, 0.5], weights: [0, 1], y: 108, order: 2 },
          { label: 'RK4 (Classic)', col: '#2E75B6', stages: [0, 0.5, 0.5, 1], weights: [1/6, 1/3, 1/3, 1/6], y: 174, order: 4 },
          { label: 'RK45 (Dormand-Prince)', col: '#27ae60', stages: [0, 0.2, 0.3, 0.6, 1, 0.875, 1], weights: [35/384, 0, 500/1113, 125/192, -2187/6784, 11/84, 0], y: 240, order: '4/5' },
        ].map(({ label, col, stages, weights, y, order }) => {
          const ox = 180, ow = 360;
          return (
            <g key={label}>
              {/* Label */}
              <text x={4} y={y + 14} fontSize="10.5" fill={col} fontWeight="700">{label}</text>
              <text x={4} y={y + 28} fontSize="9" fill="#888">Order {order}</text>

              {/* Timeline base */}
              <line x1={ox} y1={y + 18} x2={ox + ow} y2={y + 18} stroke="#ddd" strokeWidth="2" />
              <text x={ox - 4} y={y + 22} textAnchor="end" fontSize="9" fill="#aaa">0</text>
              <text x={ox + ow + 4} y={y + 22} fontSize="9" fill="#aaa">1</text>

              {/* Stage dots */}
              {stages.map((c, si) => {
                const x = ox + c * ow;
                const w = weights[si];
                const barH = Math.abs(w) * 36 + (w !== 0 ? 4 : 0);
                return (
                  <g key={si}>
                    <circle cx={x} cy={y + 18} r={5} fill={col} opacity={0.7 + si * 0.05} />
                    {w !== 0 && (
                      <rect x={x - 4} y={w > 0 ? y - barH + 14 : y + 18}
                        width={8} height={barH} fill={col} opacity="0.25" rx={2} />
                    )}
                    <text x={x} y={y + 34} textAnchor="middle" fontSize="8" fill={col}>k{si + 1}</text>
                    {w !== 0 && (
                      <text x={x} y={y - barH + 10} textAnchor="middle" fontSize="7.5" fill={col}>{w.toFixed(2)}</text>
                    )}
                  </g>
                );
              })}
            </g>
          );
        })}

        {/* Butcher tableau for RK4 */}
        {(() => {
          const tx = 580, ty = 20;
          const cArr = [0, '1/2', '1/2', 1];
          const aArr = [
            ['', '', '', ''],
            ['1/2', '', '', ''],
            ['0', '1/2', '', ''],
            ['0', '0', '1', ''],
          ];
          const bArr = ['1/6', '1/3', '1/3', '1/6'];
          const cellW = 38, cellH = 24;

          return (
            <g>
              <text x={tx + 110} y={ty + 12} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">Butcher Tableau — RK4</text>

              {/* c column */}
              {cArr.map((c, i) => (
                <g key={i}>
                  <rect x={tx} y={ty + 16 + i * cellH} width={cellW} height={cellH} fill="#FEF0F0" stroke="#f0cece" strokeWidth="0.5" />
                  <text x={tx + cellW / 2} y={ty + 16 + i * cellH + 16} textAnchor="middle" fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">{String(c)}</text>
                </g>
              ))}

              {/* a matrix */}
              {aArr.map((row, ri) => row.map((val, ci) => {
                const x = tx + cellW + ci * cellW;
                const y = ty + 16 + ri * cellH;
                return (
                  <g key={`${ri}-${ci}`}>
                    <rect x={x} y={y} width={cellW} height={cellH} fill={val ? '#eaf3ff' : '#f9f9f9'} stroke="#f0cece" strokeWidth="0.5" />
                    <text x={x + cellW / 2} y={y + 16} textAnchor="middle" fontSize="9.5" fill="#2E75B6" fontFamily="'Courier New',monospace">{val || ''}</text>
                  </g>
                );
              }))}

              {/* b row */}
              {bArr.map((b, i) => {
                const x = tx + cellW + i * cellW;
                const y = ty + 16 + 4 * cellH;
                return (
                  <g key={i}>
                    <rect x={x} y={y} width={cellW} height={cellH} fill="#f0fff4" stroke="#27ae60" strokeWidth="1" />
                    <text x={x + cellW / 2} y={y + 16} textAnchor="middle" fontSize="9.5" fill="#27ae60" fontFamily="'Courier New',monospace">{b}</text>
                  </g>
                );
              })}

              {/* Labels */}
              <text x={tx + cellW / 2} y={ty + 16 + 4 * cellH + 34} textAnchor="middle" fontSize="9" fill="#c0392b">c (nodes)</text>
              <text x={tx + cellW + 4 * cellW / 2} y={ty + 14} textAnchor="middle" fontSize="9" fill="#2E75B6">A (Runge-Kutta matrix)</text>
              <text x={tx + cellW + 4 * cellW / 2} y={ty + 16 + 4 * cellH + 34} textAnchor="middle" fontSize="9" fill="#27ae60">b (weights)</text>

              {/* Divider line between c and A */}
              <line x1={tx + cellW} y1={ty + 16} x2={tx + cellW} y2={ty + 16 + 5 * cellH} stroke="#888" strokeWidth="1.5" />
              <line x1={tx} y1={ty + 16 + 4 * cellH} x2={tx + 5 * cellW} y2={ty + 16 + 4 * cellH} stroke="#27ae60" strokeWidth="1.5" />
            </g>
          );
        })()}

        {/* Adaptive step note */}
        <rect x={580} y={170} width={308} height={138} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={734} y={186} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Adaptive Step-Size Control</text>
        {[
          { y: 196, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
            title: 'Error estimate (RK45):',
            lines: ['e = ||y₅ − y₄||  (two embedded orders)', 'Compare e to tolerance ε_tol'] },
          { y: 238, col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
            title: 'Step scaling factor:',
            lines: ['h_new = h · (ε_tol/e)^(1/5) · safety', 'safety ≈ 0.9;  limit: 0.1 ≤ scale ≤ 5'] },
          { y: 280, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            title: 'Benefit:',
            lines: ['Small steps at rapid change,', 'large steps where solution is smooth'] },
        ].map(({ y, col, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={586} y={y} width={296} height={lines.length * 16 + 18} rx={3} fill={bg} stroke={bc} strokeWidth="1" />
            <text x={592} y={y + 13} fontSize="10" fill={col} fontWeight="700">{title}</text>
            {lines.map((line, li) => (
              <text key={li} x={592} y={y + 26 + li * 16} fontSize="9.5" fill="#333">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The Runge-Kutta family evaluates the derivative f(t, y) at multiple intermediate points (stages) within each step. Higher-order methods use more stages but achieve dramatically better accuracy per step. The Butcher tableau is a compact notation: the c-vector holds the stage time offsets, the A-matrix holds the inter-stage coupling coefficients, and the b-vector holds the final weighted sum. RK45 (Dormand-Prince) pairs a 4th-order and 5th-order solution to estimate the local error, enabling efficient adaptive step-size control — the backbone of MATLAB ode45, Python scipy.integrate, and most production ODE solvers.</div>
    </div>
  );
}

// ── Diagram 3: Stability Regions of ODE Integrators ───────────────
function DiagramStabilityRegions() {
  const W = 900, H = 340;
  const PW = (W - 30) / 2;

  // Stability boundary for Euler: |1 + h*lambda| = 1  (circle of radius 1 centred at -1)
  // For RK2: |1 + z + z²/2| = 1
  // For RK4: |1 + z + z²/2 + z³/6 + z⁴/24| = 1
  // Plot in the z = h*lambda (complex) plane

  const N = 360;
  const theta = Array.from({ length: N }, (_, i) => i / N * 2 * Math.PI);

  // Find |stability polynomial| = 1 contour by evaluation on grid
  const gridN = 200;
  const xRange = [-5, 2], yRange = [-4, 4];
  const cx1 = 10 + PW / 2, cy1 = 170, scX = 40, scY = 38;

  const toXz = (re, cx) => cx + re * scX;
  const toYz = (im, cy) => cy - im * scY;

  const stabilityPoly = (z_re, z_im, order) => {
    // R(z) = sum of Taylor series of e^z truncated at order
    let re = 1, im = 0;
    let zr = z_re, zi = z_im;
    let fac = 1;
    let termR = 1, termI = 0;
    for (let k = 1; k <= order; k++) {
      fac *= k;
      // multiply term by z
      const newR = termR * zr - termI * zi;
      const newI = termR * zi + termI * zr;
      termR = newR / fac * (k === 1 ? 1 : k - 1);
      termI = newI / fac * (k === 1 ? 1 : k - 1);
      // recalculate properly
      let pR = 1, pI = 0;
      for (let j = 0; j < k; j++) {
        const nr = pR * zr - pI * zi;
        const ni = pR * zi + pI * zr;
        pR = nr; pI = ni;
      }
      let fact = 1;
      for (let j = 1; j <= k; j++) fact *= j;
      re += pR / fact;
      im += pI / fact;
      if (k === 1) { re -= pR / fact; im -= pI / fact; }
    }
    // redo properly
    let R = 1, I = 0;
    let powR = 1, powI = 0;
    let factorial = 1;
    for (let k = 1; k <= order; k++) {
      factorial *= k;
      const nr = powR * z_re - powI * z_im;
      const ni = powR * z_im + powI * z_re;
      powR = nr; powI = ni;
      R += powR / factorial;
      I += powI / factorial;
    }
    return Math.sqrt(R * R + I * I);
  };

  // Trace stability boundaries numerically
  const traceBoundary = (order) => {
    const pts = [];
    for (let ri = -5; ri <= 2; ri += 0.05) {
      for (let ii = -3.9; ii <= 3.9; ii += 0.05) {
        const mag = stabilityPoly(ri, ii, order);
        if (Math.abs(mag - 1) < 0.04) {
          pts.push([ri, ii]);
        }
      }
    }
    return pts;
  };

  const eulerBoundary   = traceBoundary(1);
  const rk2Boundary     = traceBoundary(2);
  const rk4Boundary     = traceBoundary(4);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Stability Regions of ODE Integrators in the Complex z = h·λ Plane</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="sr1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Panel 1: Stability regions */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={cx1} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Stability Regions — Must Contain All h·λ</text>
        <text x={cx1} y={42} textAnchor="middle" fontSize="10" fill="#555">Stable iff |R(hλ)| ≤ 1;  λ = eigenvalues of system Jacobian</text>

        {/* Axes */}
        <line x1={10 + 14} y1={cy1} x2={10 + PW - 10} y2={cy1} stroke="#ddd" strokeWidth="1.5" markerEnd="url(#sr1)" />
        <line x1={cx1} y1={H - 24} x2={cx1} y2={16} stroke="#ddd" strokeWidth="1.5" markerEnd="url(#sr1)" />
        <text x={10 + PW - 12} y={cy1 + 14} fontSize="10" fill="#aaa">Re(hλ)</text>
        <text x={cx1 + 4} y={20} fontSize="10" fill="#aaa">Im(hλ)</text>

        {/* Grid */}
        {[-4, -3, -2, -1, 0, 1, 2].map(v => (
          <g key={v}>
            <line x1={toXz(v, cx1)} y1={cy1 - 4} x2={toXz(v, cx1)} y2={cy1 + 4} stroke="#ddd" strokeWidth="1" />
            <text x={toXz(v, cx1)} y={cy1 + 14} textAnchor="middle" fontSize="9" fill="#ccc">{v}</text>
          </g>
        ))}
        {[-3, -2, -1, 1, 2, 3].map(v => (
          <text key={v} x={cx1 - 16} y={toYz(v, cy1) + 4} textAnchor="end" fontSize="9" fill="#ccc">{v}j</text>
        ))}

        {/* Left half-plane stable region */}
        <rect x={10 + 14} y={16} width={cx1 - 10 - 14} height={H - 40} fill="#f0fff4" opacity="0.3" />
        <text x={cx1 - 80} y={cy1 + 30} fontSize="9" fill="#27ae60" opacity="0.6">LHP (stable ODE)</text>

        {/* RHS unstable warning */}
        <rect x={cx1} y={16} width={10 + PW - cx1} height={H - 40} fill="#fff0f0" opacity="0.2" />

        {/* Stability boundary points */}
        {[
          { pts: eulerBoundary,  col: '#c0392b', label: 'Euler RK1' },
          { pts: rk2Boundary,   col: '#e07b00', label: 'RK2' },
          { pts: rk4Boundary,   col: '#2E75B6', label: 'RK4' },
        ].map(({ pts, col, label }) => (
          <g key={label}>
            {pts.map(([re, im], i) => (
              <circle key={i} cx={toXz(re, cx1)} cy={toYz(im, cy1)} r={1.2} fill={col} opacity="0.7" />
            ))}
          </g>
        ))}

        {/* Imaginary axis restriction note */}
        {(() => {
          // RK4 stability on imaginary axis: up to ~2.8i
          const limY = toYz(2.8, cy1);
          const limX = cx1;
          return (
            <g>
              <line x1={limX} y1={cy1} x2={limX} y2={limY} stroke="#2E75B6" strokeWidth="2.5" opacity="0.8" />
              <text x={limX + 4} y={limY - 4} fontSize="9.5" fill="#2E75B6" fontWeight="700">RK4 jω limit</text>
              <text x={limX + 4} y={limY + 8} fontSize="9" fill="#2E75B6">≈ ±2.83i</text>
            </g>
          );
        })()}

        {/* Legend */}
        {[
          { col: '#c0392b', label: 'Euler (RK1): small disk |z+1|≤1' },
          { col: '#e07b00', label: 'RK2: larger region' },
          { col: '#2E75B6', label: 'RK4: widest explicit region' },
        ].map(({ col, label }, i) => (
          <g key={i}>
            <circle cx={18} cy={cy1 + 42 + i * 18} r={4} fill={col} />
            <text x={26} y={cy1 + 46 + i * 18} fontSize="9.5" fill={col}>{label}</text>
          </g>
        ))}

        {/* Panel 2: Stiffness and implicit methods */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Stiff Systems and Implicit Integrators</text>

        {[
          { y: 42, col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
            title: 'Stiff problem definition',
            lines: [
              'Eigenvalues λ span a very wide range: |λ_max|/|λ_min| >> 1',
              'Fast modes decay quickly; slow modes persist',
              'Explicit methods need h < 2/|λ_max|  → tiny step for stability',
              'Example: chemical kinetics, structural with rigid links',
            ] },
          { y: 138, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            title: 'Backward Euler (implicit RK1)',
            lines: [
              'y_{n+1} = yₙ + h·f(t_{n+1}, y_{n+1})',
              'Unconditionally stable: stability region = entire LHP',
              'Each step: solve nonlinear equation → Newton iteration',
              'Order 1, first-order accurate — low cost per step',
            ] },
          { y: 234, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
            title: 'Trapezoidal / Crank-Nicolson',
            lines: [
              'y_{n+1} = yₙ + h/2·[f(tₙ,yₙ) + f(t_{n+1}, y_{n+1})]',
              'A-stable, 2nd-order;  imaginary axis on stability boundary',
              'Popular for structural dynamics (conserves energy)',
              'Newmark-β with β=0.25 γ=0.5 is equivalent',
            ] },
        ].map(({ y, col, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={PW + 26} y={y} width={PW - 14} height={lines.length * 18 + 20} rx={3} fill={bg} stroke={bc} strokeWidth="1.2" />
            <text x={PW + 32} y={y + 15} fontSize="11" fill={col} fontWeight="700">{title}</text>
            {lines.map((line, li) => (
              <text key={li} x={PW + 32} y={y + 30 + li * 18} fontSize="9.5" fill="#333">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The stability region is the set of complex values z = hλ for which the numerical solution remains bounded. Explicit methods (Euler, RK4) have finite stability regions — if any eigenvalue pushes hλ outside the region, the solution blows up regardless of accuracy. RK4 is stable on the imaginary axis up to |z| ≈ 2.83, setting a maximum step size for undamped oscillators. Stiff systems with widely-separated eigenvalues force explicit methods to use very small steps; implicit methods with unlimited stability regions (A-stable) allow large steps even for stiff ODEs, at the cost of solving a nonlinear system each step.</div>
    </div>
  );
}

// ── Diagram 4: Newmark-β Method for Structural Dynamics ───────────
function DiagramNewmark() {
  const W = 900, H = 310;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Newmark-β Method — The Standard Structural Dynamics Time Integrator</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="nw1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
        </defs>

        {/* Newmark predictor-corrector diagram */}
        {(() => {
          const steps = [
            { x: 10,  label: 'Known at tₙ:\nuₙ, u̇ₙ, üₙ', col: '#27ae60', bg: '#f0fff4', bc: '#27ae60', w: 130 },
            { x: 152, label: 'Predict u̇, u\nat t_{n+1} (no corr.)', col: '#e07b00', bg: '#fff9e6', bc: '#e07b00', w: 148 },
            { x: 312, label: 'Solve EOM\nfor ü_{n+1}', col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b', w: 130 },
            { x: 454, label: 'Correct u̇, u\nusing ü_{n+1}', col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6', w: 140 },
            { x: 606, label: 'Next step:\ntₙ → tₙ + h', col: '#27ae60', bg: '#f0fff4', bc: '#27ae60', w: 120 },
          ];
          return (
            <>
              {steps.map(({ x, label, col, bg, bc, w }, i) => (
                <g key={i}>
                  <rect x={x} y={12} width={w} height={50} rx={5} fill={bg} stroke={bc} strokeWidth="2" />
                  {label.split('\n').map((line, li) => (
                    <text key={li} x={x + w / 2} y={30 + li * 16} textAnchor="middle" fontSize="10.5" fill={col} fontWeight="700">{line}</text>
                  ))}
                  {i < steps.length - 1 && (
                    <line x1={x + w} y1={37} x2={x + w + 10} y2={37} stroke="#c0392b" strokeWidth="2" markerEnd="url(#nw1)" />
                  )}
                </g>
              ))}
            </>
          );
        })()}

        {/* Equations */}
        {[
          { y: 76, col: '#e07b00', bg: '#fff9e6', bc: '#e07b00', title: 'Predictor (no u̇/u correction yet):',
            eqs: [
              'ũ_{n+1} = uₙ + h·u̇ₙ + h²(0.5−β)·üₙ',
              'u̇̃_{n+1} = u̇ₙ + h(1−γ)·üₙ',
            ] },
          { y: 140, col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b', title: 'Solve EOM for ü_{n+1}:',
            eqs: [
              'M·ü_{n+1} + C·u̇_{n+1} + K·u_{n+1} = F_{n+1}',
              'Substitute predictors → solve for ü_{n+1}',
              'Effective stiffness: K_eff = K + γ/(βh)·C + 1/(βh²)·M',
            ] },
          { y: 220, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6', title: 'Corrector:',
            eqs: [
              'u_{n+1} = ũ_{n+1} + β·h²·ü_{n+1}',
              'u̇_{n+1} = u̇̃_{n+1} + γ·h·ü_{n+1}',
            ] },
        ].map(({ y, col, bg, bc, title, eqs }) => (
          <g key={y}>
            <rect x={10} y={y} width={560} height={eqs.length * 18 + 22} rx={3} fill={bg} stroke={bc} strokeWidth="1.5" />
            <text x={16} y={y + 15} fontSize="10.5" fill={col} fontWeight="700">{title}</text>
            {eqs.map((eq, i) => (
              <text key={i} x={16} y={y + 30 + i * 18} fontSize="10" fill="#333" fontFamily="'Courier New',monospace">{eq}</text>
            ))}
          </g>
        ))}

        {/* β, γ table */}
        <rect x={582} y={76} width={306} height={H - 84} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={735} y={94} textAnchor="middle" fontSize="11" fill="#7B0D0D" fontWeight="700">Parameter Choices (β, γ)</text>
        {[
          { b: '0,  1/2', name: 'Central difference', stable: 'Conditional', note: 'Explicit — no solve' },
          { b: '1/4, 1/2', name: 'Average accel. (trap.)', stable: 'Unconditional', note: 'No numerical damp.' },
          { b: '1/6, 1/2', name: 'Linear accel.', stable: 'Conditional', note: 'ρ_s < √3 only' },
          { b: '1/4+α, 1/2+α', name: 'HHT-α / Generalized-α', stable: 'Unconditional', note: 'Controllable damp.' },
        ].map(({ b, name, stable, note }, i) => {
          const ry = 104 + i * 44;
          const bg = i % 2 === 0 ? '#fff5f5' : '#fff';
          return (
            <g key={i}>
              <rect x={588} y={ry} width={294} height={40} fill={bg} stroke="#f0cece" strokeWidth="0.5" />
              <text x={594} y={ry + 14} fontSize="9.5" fill="#7B0D0D" fontWeight="700" fontFamily="'Courier New',monospace">β,γ = {b}</text>
              <text x={594} y={ry + 28} fontSize="9" fill="#555">{name}</text>
              <text x={750} y={ry + 14} fontSize="9" fill={stable === 'Unconditional' ? '#27ae60' : '#c0392b'} fontWeight="700">{stable}</text>
              <text x={750} y={ry + 28} fontSize="9" fill="#777">{note}</text>
            </g>
          );
        })}

        <rect x={588} y={282} width={294} height={16} rx={2} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1" />
        <text x={594} y={293} fontSize="9.5" fill="#7B0D0D" fontWeight="700">Stability: h ≤ 2/ωₙ for conditional; no limit for unconditional</text>
      </svg>
      <div style={S.dCaption}>The Newmark-β method is the most widely used time integrator in structural dynamics (FEA, earthquake engineering, impact). It interpolates acceleration within each step using parameters β (displacement weighting) and γ (velocity weighting). The average acceleration method (β=1/4, γ=1/2) is unconditionally stable and second-order accurate with no numerical damping — the default in most FEA codes. The HHT-α (Hilber-Hughes-Taylor) generalisation adds controllable numerical damping to suppress high-frequency spurious oscillations without affecting low-frequency accuracy.</div>
    </div>
  );
}

// ── Diagram 5: Finite Element Method for Vibrations ───────────────
function DiagramFEM() {
  const W = 900, H = 320;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Finite Element Method — Assembly, Mass/Stiffness Matrices, and Eigenvalue Problem</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="fe1" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
        </defs>

        {/* FEM beam with elements */}
        {(() => {
          const beamY = 80, beamX = 20, beamW = 560, nElem = 5;
          const elemW = beamW / nElem;
          const nodeX = Array.from({ length: nElem + 1 }, (_, i) => beamX + i * elemW);
          const cols = ['#c0392b', '#e07b00', '#2E75B6', '#27ae60', '#9b59b6'];

          return (
            <g>
              {/* Beam */}
              <rect x={beamX} y={beamY - 12} width={beamW} height={24} fill="#f5f5f5" stroke="#888" strokeWidth="2" rx={3} />

              {/* Elements (coloured) */}
              {Array.from({ length: nElem }, (_, i) => (
                <g key={i}>
                  <rect x={beamX + i * elemW + 2} y={beamY - 10} width={elemW - 4} height={20}
                    fill={cols[i]} opacity="0.15" rx={2} />
                  <text x={beamX + (i + 0.5) * elemW} y={beamY + 5} textAnchor="middle"
                    fontSize="10" fill={cols[i]} fontWeight="700">e{i + 1}</text>
                </g>
              ))}

              {/* Nodes */}
              {nodeX.map((x, i) => (
                <g key={i}>
                  <circle cx={x} cy={beamY} r={6} fill="#c0392b" stroke="#fff" strokeWidth="1.5" />
                  <text x={x} y={beamY + 22} textAnchor="middle" fontSize="9.5" fill="#c0392b" fontWeight="700">N{i + 1}</text>
                  {/* DOF arrows */}
                  <line x1={x} y1={beamY - 12} x2={x} y2={beamY - 26} stroke="#2E75B6" strokeWidth="1.5" markerEnd="url(#fe1)" />
                  <text x={x} y={beamY - 28} textAnchor="middle" fontSize="8.5" fill="#2E75B6">u{i + 1}</text>
                </g>
              ))}

              {/* Fixed support */}
              <rect x={beamX - 12} y={beamY - 20} width={12} height={40} fill="#888" rx={2} />
              {[-12, 0, 12].map(dy => (
                <line key={dy} x1={beamX - 12} y1={beamY + dy} x2={beamX - 22} y2={beamY + dy + 10} stroke="#bbb" strokeWidth="1.5" />
              ))}
            </g>
          );
        })()}

        {/* Element stiffness/mass matrices */}
        <text x={10} y={130} fontSize="10.5" fill="#7B0D0D" fontWeight="700">Element bar: kₑ = (EA/L)[1 −1; −1 1],  mₑ = (ρAL/6)[2 1; 1 2]  (consistent mass)</text>

        {/* Assembly diagram */}
        {(() => {
          const n = 4; // 4 DOF for illustration
          const cellS = 28, ox = 10, oy = 148;
          const label = 'K (assembled, 4×4 banded)';

          // Show sparsity pattern
          const nonzero = new Set(['0-0','0-1','1-0','1-1','1-2','2-1','2-2','2-3','3-2','3-3']);

          return (
            <g>
              <text x={ox} y={oy - 6} fontSize="10.5" fill="#c0392b" fontWeight="700">{label}</text>
              {Array.from({ length: n }, (_, i) => Array.from({ length: n }, (_, j) => {
                const key = `${i}-${j}`;
                const isDiag = i === j;
                const isNear = Math.abs(i - j) === 1;
                const fill = isDiag ? '#c0392b' : isNear ? '#FEF0F0' : '#f9f9f9';
                const stroke = isDiag ? '#c0392b' : isNear ? '#f0cece' : '#f0f0f0';
                return (
                  <g key={key}>
                    <rect x={ox + j * cellS} y={oy + i * cellS} width={cellS - 1} height={cellS - 1}
                      fill={fill} stroke={stroke} strokeWidth="1" rx={2} />
                    {(isDiag || isNear) && (
                      <text x={ox + j * cellS + cellS / 2} y={oy + i * cellS + cellS / 2 + 4}
                        textAnchor="middle" fontSize={isDiag ? '9' : '8.5'}
                        fill={isDiag ? '#fff' : '#c0392b'} fontWeight={isDiag ? '700' : '400'}>
                        {isDiag ? 'kᵢᵢ' : 'kᵢⱼ'}
                      </text>
                    )}
                  </g>
                );
              }))}

              {/* Arrow to global */}
              <text x={ox + n * cellS + 12} y={oy + 56} fontSize="9.5" fill="#555" fontStyle="italic">Banded / sparse</text>
              <text x={ox + n * cellS + 12} y={oy + 70} fontSize="9.5" fill="#555" fontStyle="italic">structure</text>
            </g>
          );
        })()}

        {/* Eigenvalue problem */}
        <rect x={10} y={280} width={560} height={30} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
        <text x={16} y={293} fontSize="10" fill="#7B0D0D" fontWeight="700">Global eigenvalue problem:  [K − ωₙ²M]φ = 0  →  natural frequencies + mode shapes</text>
        <text x={16} y={305} fontSize="9.5" fill="#555">Solved by: LAPACK (dsygv), Lanczos (large sparse), Subspace iteration, or FEAST algorithm</text>

        {/* Right panel: FEM workflow */}
        <rect x={590} y={10} width={298} height={H - 18} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={739} y={28} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">FEM Workflow</text>
        {[
          { y: 36, col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b', step: '① Geometry + Mesh',
            desc: 'Divide domain into elements\nRefine where stress/motion highest' },
          { y: 90, col: '#e07b00', bg: '#fff9e6', bc: '#e07b00', step: '② Element Matrices',
            desc: 'Compute kₑ, mₑ from shape funcs\nGauss quadrature for integrals' },
          { y: 144, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6', step: '③ Assembly',
            desc: 'Map elements to global DOFs\nAssemble banded sparse [K],[M]' },
          { y: 198, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60', step: '④ Apply BCs',
            desc: 'Constrain fixed DOFs\nPenalty or elimination method' },
          { y: 252, col: '#9b59b6', bg: '#f8f0ff', bc: '#9b59b6', step: '⑤ Solve',
            desc: 'Eigenvalue (modes) or\nTime integration (dynamic)' },
        ].map(({ y, col, bg, bc, step, desc }) => (
          <g key={y}>
            <rect x={596} y={y} width={286} height={50} rx={3} fill={bg} stroke={bc} strokeWidth="1" />
            <text x={602} y={y + 15} fontSize="10.5" fill={col} fontWeight="700">{step}</text>
            {desc.split('\n').map((line, li) => (
              <text key={li} x={602} y={y + 30 + li * 14} fontSize="9.5" fill="#333">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The finite element method discretises a continuous structure into elements, each contributing local stiffness and mass matrices assembled into global banded matrices [K] and [M]. Shape functions interpolate displacements within each element; consistent mass matrices use the same shape functions for both stiffness and inertia. The global eigenvalue problem [K − ωₙ²M]φ = 0 yields natural frequencies and mode shapes. For large models (millions of DOF), Lanczos or FEAST algorithms extract only the lowest modes efficiently. Dynamic response uses Newmark or HHT integration of the assembled equations of motion.</div>
    </div>
  );
}

// ── Diagram 6: Error Analysis — Convergence and Richardson ────────
function DiagramErrorAnalysis() {
  const W = 900, H = 340;
  const PW = (W - 30) / 2;

  // Convergence plot: error vs dt on log-log scale
  const dtVals = [0.5, 0.25, 0.125, 0.0625, 0.03125];
  // True solution y(1) = trueSol(1) for y' = -y + sin t
  const trueSol = t => 0.5 * (Math.sin(t) - Math.cos(t)) + 0.5 * Math.exp(-t);
  const f2 = (t, y) => -y + Math.sin(t);
  const tFinal = 4;

  const eulerErrors = dtVals.map(dt => {
    let y = 0, t = 0;
    while (t < tFinal - 1e-10) {
      y += dt * f2(t, y);
      t += dt;
    }
    return Math.abs(y - trueSol(tFinal));
  });

  const rk2Errors = dtVals.map(dt => {
    let y = 0, t = 0;
    while (t < tFinal - 1e-10) {
      const k1 = f2(t, y);
      const k2 = f2(t + dt, y + dt * k1);
      y += dt / 2 * (k1 + k2);
      t += dt;
    }
    return Math.abs(y - trueSol(tFinal));
  });

  const rk4Errors = dtVals.map(dt => {
    let y = 0, t = 0;
    while (t < tFinal - 1e-10) {
      const k1 = f2(t, y);
      const k2 = f2(t + dt / 2, y + dt / 2 * k1);
      const k3 = f2(t + dt / 2, y + dt / 2 * k2);
      const k4 = f2(t + dt, y + dt * k3);
      y += dt / 6 * (k1 + 2 * k2 + 2 * k3 + k4);
      t += dt;
    }
    return Math.abs(y - trueSol(tFinal));
  });

  const ox = 40, oy = 240, ow = PW - 54, oh = 190;
  const dtMin = 0.03, dtMax = 0.6, eMin = 1e-9, eMax = 0.5;
  const toXlog = dt => ox + Math.log10(dt / dtMin) / Math.log10(dtMax / dtMin) * ow;
  const toYlog = e => oy - Math.log10(Math.max(e, 1e-12) / eMin) / Math.log10(eMax / eMin) * oh;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Convergence Analysis — Global Error vs. Step Size (log-log) and Richardson Extrapolation</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="ea1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Panel 1: Convergence plot */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Global Error vs. Step Size — log-log Scale</text>

        {/* Grid */}
        {[-1, -3, -5, -7, -9].map(exp => {
          const y = toYlog(Math.pow(10, exp));
          if (y < 30 || y > oy + 4) return null;
          return (
            <g key={exp}>
              <line x1={ox} y1={y} x2={ox + ow} y2={y} stroke="#f5f5f5" strokeWidth="1" />
              <text x={ox - 4} y={y + 4} textAnchor="end" fontSize="8.5" fill="#aaa">1e{exp}</text>
            </g>
          );
        })}
        {dtVals.map(dt => {
          const x = toXlog(dt);
          return (
            <g key={dt}>
              <line x1={x} y1={30} x2={x} y2={oy} stroke="#f5f5f5" strokeWidth="1" />
              <text x={x} y={oy + 14} textAnchor="middle" fontSize="8.5" fill="#aaa">{dt}</text>
            </g>
          );
        })}

        {/* Reference slopes */}
        {[
          { slope: 1, col: '#c0392b', dash: '4,2', label: 'slope 1 (O(dt))' },
          { slope: 2, col: '#e07b00', dash: '4,2', label: 'slope 2 (O(dt²))' },
          { slope: 4, col: '#2E75B6', dash: '4,2', label: 'slope 4 (O(dt⁴))' },
        ].map(({ slope, col, dash, label }) => {
          const x1 = toXlog(0.5), x2 = toXlog(0.03125);
          const y1 = toYlog(0.4);
          const y2 = y1 - slope * (x2 - x1) / (Math.log10(0.5 / 0.03125)) * oh / (Math.log10(eMax / eMin)) * Math.log10(dtMax / dtMin);
          return (
            <g key={slope}>
              <line x1={x1} y1={y1} x2={x2} y2={y2 - (slope - 1) * 30} stroke={col} strokeWidth="1.2" strokeDasharray={dash} opacity="0.5" />
            </g>
          );
        })}

        {/* Error curves */}
        {[
          { errs: eulerErrors, col: '#c0392b', label: 'Euler O(dt)' },
          { errs: rk2Errors,   col: '#e07b00', label: 'RK2 O(dt²)' },
          { errs: rk4Errors,   col: '#2E75B6', label: 'RK4 O(dt⁴)' },
        ].map(({ errs, col, label }) => {
          const pts = dtVals.map((dt, i) => `${toXlog(dt)},${toYlog(errs[i])}`).join(' ');
          return (
            <g key={label}>
              <polyline points={pts} fill="none" stroke={col} strokeWidth="2.2" />
              {dtVals.map((dt, i) => (
                <circle key={i} cx={toXlog(dt)} cy={toYlog(errs[i])} r={4} fill={col} />
              ))}
            </g>
          );
        })}

        {/* Axes */}
        <line x1={ox} y1={oy} x2={ox + ow + 8} y2={oy} stroke="#888" strokeWidth="1.5" markerEnd="url(#ea1)" />
        <line x1={ox} y1={30} x2={ox} y2={oy + 4} stroke="#888" strokeWidth="1.5" />
        <text x={ox + ow / 2} y={oy + 26} textAnchor="middle" fontSize="10" fill="#555">Step size dt (log scale)</text>
        <text x={ox - 20} y={oy - oh / 2} fontSize="9.5" fill="#555" transform={`rotate(-90,${ox - 20},${oy - oh / 2})`} textAnchor="middle">Global error (log scale)</text>

        {/* Legend */}
        {[
          { col: '#c0392b', label: 'Euler RK1' },
          { col: '#e07b00', label: 'RK2 (Midpoint)' },
          { col: '#2E75B6', label: 'RK4 (Classic)' },
        ].map(({ col, label }, i) => (
          <g key={i}>
            <circle cx={ox + 8} cy={H - 44 + i * 16} r={4} fill={col} />
            <text x={ox + 16} y={H - 40 + i * 16} fontSize="9.5" fill={col}>{label}</text>
          </g>
        ))}

        {/* Panel 2: Richardson extrapolation */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Richardson Extrapolation and Error Estimation</text>

        {[
          { y: 40, col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
            title: 'Richardson Extrapolation',
            lines: [
              'Run solver at h and h/2; call results y_h and y_{h/2}',
              'For p-th order method: y_exact ≈ y_{h/2} + (y_{h/2}−y_h)/(2^p−1)',
              'Eliminates leading error term: improves order by 1',
              'Error estimate: |e| ≈ |y_{h/2}−y_h| / (2^p−1)',
            ] },
          { y: 138, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            title: 'Embedded Methods (Adaptive)',
            lines: [
              'Run two methods of order p and p+1 simultaneously',
              'Error estimate = difference between two solutions',
              'RK45 (Dormand-Prince): 6 stages for both 4th and 5th order',
              'Cost: barely more than single order p+1 run',
              'Step control: h_new = h·(tol/err)^{1/(p+1)} · 0.9',
            ] },
          { y: 252, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
            title: 'Practical Error Control',
            lines: [
              'Absolute tolerance: |eᵢ| < atol (small solutions)',
              'Relative tolerance: |eᵢ/yᵢ| < rtol (large solutions)',
              'Mixed: |eᵢ/(atol + rtol·|yᵢ|)| < 1 (recommended)',
              'Typical: atol=1e-6, rtol=1e-3 for engineering ODEs',
            ] },
        ].map(({ y, col, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={PW + 26} y={y} width={PW - 14} height={lines.length * 18 + 20} rx={3} fill={bg} stroke={bc} strokeWidth="1.2" />
            <text x={PW + 32} y={y + 15} fontSize="11" fill={col} fontWeight="700">{title}</text>
            {lines.map((line, li) => (
              <text key={li} x={PW + 32} y={y + 30 + li * 18} fontSize="9.5" fill="#333">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>The convergence plot on a log-log scale reveals the order of a numerical method as the slope of the error-vs-step-size line. A first-order method (Euler) has slope 1; second-order (RK2) has slope 2; fourth-order (RK4) has slope 4. Richardson extrapolation exploits this known error structure: by comparing solutions at h and h/2, the leading error term can be eliminated to obtain a higher-order estimate at negligible additional cost. Adaptive step-size methods use embedded Runge-Kutta pairs to estimate the local error cheaply at every step, automatically adjusting h to meet the user-specified tolerance.</div>
    </div>
  );
}

// ── Diagram 7: Frequency-Domain Methods — DFT and FFT ─────────────
function DiagramFFT() {
  const W = 900, H = 330;
  const PW = (W - 30) / 2;

  // Signal: two sinusoids + noise
  const N = 64;
  let rng = 12345;
  const lcg = () => { rng = (rng * 1103515245 + 12345) & 0x7fffffff; return rng / 0x7fffffff; };

  const fs = 64;
  const t = Array.from({ length: N }, (_, i) => i / fs);
  const signal = t.map(tv =>
    1.2 * Math.cos(2 * Math.PI * 8 * tv) +
    0.6 * Math.cos(2 * Math.PI * 20 * tv + 0.8) +
    0.2 * (lcg() - 0.5)
  );

  // DFT magnitudes (analytic — skip full DFT for brevity)
  const freqBins = Array.from({ length: N / 2 }, (_, k) => k * fs / N);
  const dftMag = freqBins.map(f => {
    let re = 0, im = 0;
    for (let n = 0; n < N; n++) {
      re += signal[n] * Math.cos(2 * Math.PI * f / fs * n);
      im -= signal[n] * Math.sin(2 * Math.PI * f / fs * n);
    }
    return Math.sqrt(re * re + im * im) / N * 2;
  });

  const ox1 = 14, oy1 = 160, ow1 = PW - 28, oh1 = 110;
  const toXt = tv => ox1 + tv / (N / fs) * ow1;
  const toYt = v => oy1 - Math.max(-2, Math.min(2, v)) * oh1 / 2;

  const ox2 = PW + 36, oy2 = 260, ow2 = PW - 50, oh2 = 200;
  const toXf = f => ox2 + f / 32 * ow2;
  const toYm = m => oy2 - Math.min(m / 1.4, 1) * oh2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>DFT / FFT — Frequency Analysis and Spectral Leakage in Structural Dynamics</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="fft1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
        </defs>

        {/* Panel 1: Time signal */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Time Signal — 8 Hz + 20 Hz + Noise</text>

        <polyline
          points={signal.map((v, i) => `${toXt(t[i])},${toYt(v)}`).join(' ')}
          fill="none" stroke="#c0392b" strokeWidth="1.8"
        />
        <line x1={ox1} y1={oy1} x2={ox1 + ow1 + 8} y2={oy1} stroke="#888" strokeWidth="1.5" markerEnd="url(#fft1)" />
        <line x1={ox1} y1={oy1 + oh1 / 2 + 4} x2={ox1} y2={oy1 - oh1 / 2 - 8} stroke="#888" strokeWidth="1.5" markerEnd="url(#fft1)" />
        <text x={ox1 + ow1 + 12} y={oy1 + 4} fontSize="10" fill="#555">t (s)</text>
        {[0, 0.25, 0.5, 0.75, 1.0].map(tv => (
          <text key={tv} x={toXt(tv)} y={oy1 + 14} textAnchor="middle" fontSize="9" fill="#aaa">{tv}</text>
        ))}

        {/* FFT info box */}
        <rect x={14} y={H - 100} width={PW - 20} height={84} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
        <text x={20} y={H - 86} fontSize="10.5" fill="#7B0D0D" fontWeight="700">FFT Algorithm (Cooley-Tukey):</text>
        <text x={20} y={H - 72} fontSize="9.5" fill="#333" fontFamily="'Courier New',monospace">X[k] = Σ x[n]·e^(-j2πkn/N)</text>
        <text x={20} y={H - 58} fontSize="9.5" fill="#333">DFT: O(N²) operations;  FFT: O(N log₂ N) — 1000× faster for N=1024</text>
        <text x={20} y={H - 44} fontSize="9.5" fill="#333">Requires N = 2^m (power of 2);  frequency resolution: Δf = fs/N</text>
        <text x={20} y={H - 30} fontSize="9.5" fill="#555">Nyquist frequency: fₙ = fs/2 (max detectable frequency)</text>
        <text x={20} y={H - 16} fontSize="9.5" fill="#555">Zero-padding: increases spectral resolution by interpolation</text>

        {/* Panel 2: DFT spectrum */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={27} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">DFT Magnitude Spectrum — Identified Frequencies</text>

        {/* Spectrum */}
        {freqBins.map((f, i) => {
          const x = toXf(f);
          const yTop = toYm(dftMag[i]);
          if (x > ox2 + ow2) return null;
          const col = f === 8 || f === 20 ? '#c0392b' : '#2E75B6';
          return (
            <g key={i}>
              <line x1={x} y1={oy2} x2={x} y2={yTop + 2} stroke={col} strokeWidth={f === 8 || f === 20 ? 3 : 1.5} opacity={f === 8 || f === 20 ? 1 : 0.5} />
            </g>
          );
        })}

        {/* Frequency labels */}
        {[{ f: 8, amp: 1.2, label: '8 Hz (1.2)' }, { f: 20, amp: 0.6, label: '20 Hz (0.6)' }].map(({ f, amp, label }) => (
          <g key={f}>
            <circle cx={toXf(f)} cy={toYm(amp)} r={5} fill="#c0392b" stroke="#fff" strokeWidth="1.5" />
            <text x={toXf(f)} y={toYm(amp) - 8} textAnchor="middle" fontSize="9.5" fill="#c0392b" fontWeight="700">{label}</text>
          </g>
        ))}

        {/* Axes */}
        <line x1={ox2} y1={oy2 + 4} x2={ox2 + ow2 + 8} y2={oy2 + 4} stroke="#888" strokeWidth="1.5" markerEnd="url(#fft1)" />
        <line x1={ox2} y1={oy2 + 4} x2={ox2} y2={oy2 - oh2 - 8} stroke="#888" strokeWidth="1.5" markerEnd="url(#fft1)" />
        <text x={ox2 + ow2 / 2} y={oy2 + 18} textAnchor="middle" fontSize="10" fill="#555">Frequency (Hz)</text>
        {[0, 8, 16, 24, 32].map(f => (
          <text key={f} x={toXf(f)} y={oy2 + 15} textAnchor="middle" fontSize="9" fill="#aaa">{f}</text>
        ))}

        {/* Spectral leakage note */}
        <rect x={PW + 26} y={H - 64} width={PW - 14} height={48} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1" />
        <text x={PW + 32} y={H - 50} fontSize="10" fill="#7B0D0D" fontWeight="700">Spectral leakage: non-integer cycles in window smear energy</text>
        <text x={PW + 32} y={H - 36} fontSize="9.5" fill="#333">Window functions (Hann, Hamming) reduce leakage at cost of resolution</text>
        <text x={PW + 32} y={H - 22} fontSize="9.5" fill="#555">Use: Hann (general), flat-top (amplitude accuracy), rectangular (coherent)</text>
      </svg>
      <div style={S.dCaption}>The Fast Fourier Transform (FFT) computes the Discrete Fourier Transform in O(N log₂ N) operations instead of O(N²), making spectral analysis practical for large datasets. In system dynamics, the FFT identifies natural frequencies from free-decay responses, estimates frequency response functions from excitation-response pairs, and computes power spectral densities for random vibration. Key considerations: frequency resolution Δf = fs/N (longer record → finer resolution); Nyquist limit fₙ = fs/2 (must sample at least twice the highest frequency of interest); spectral leakage (non-integer cycles in window → energy spreading, mitigated by window functions).</div>
    </div>
  );
}

// ── Diagram 8: Finite Differences for PDEs ────────────────────────
function DiagramFiniteDiff() {
  const W = 900, H = 320;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Finite Differences — Wave Equation and Method of Lines</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="fd1" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
            <polygon points="0 0,6 3,0 6" fill="#888" />
          </marker>
          <marker id="fd2" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0,6 3.5,0 7" fill="#c0392b" />
          </marker>
        </defs>

        {/* Panel 1: Grid and stencil */}
        <rect x={10} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={10 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Space-Time Grid and Finite Difference Stencil</text>

        {(() => {
          const gridX = 24, gridY = 50, dx = 54, dt = 44;
          const nX = 7, nT = 5;
          const iC = 3, jC = 2; // centre node

          return (
            <g>
              {/* Grid lines */}
              {Array.from({ length: nX }, (_, i) => (
                <line key={`x${i}`} x1={gridX + i * dx} y1={gridY} x2={gridX + i * dx} y2={gridY + (nT - 1) * dt} stroke="#f0f0f0" strokeWidth="1" />
              ))}
              {Array.from({ length: nT }, (_, j) => (
                <line key={`t${j}`} x1={gridX} y1={gridY + j * dt} x2={gridX + (nX - 1) * dx} y2={gridY + j * dt} stroke="#f0f0f0" strokeWidth="1" />
              ))}

              {/* Grid nodes */}
              {Array.from({ length: nX }, (_, i) => Array.from({ length: nT }, (_, j) => {
                const cx = gridX + i * dx, cy = gridY + j * dt;
                const isCentre = i === iC && j === jC;
                const isStencil = (i === iC && Math.abs(j - jC) <= 1) || (j === jC && Math.abs(i - iC) <= 1);
                return (
                  <circle key={`${i}-${j}`} cx={cx} cy={cy} r={isCentre ? 6 : isStencil ? 5 : 3}
                    fill={isCentre ? '#c0392b' : isStencil ? '#2E75B6' : '#ddd'}
                    stroke={isCentre || isStencil ? '#fff' : 'none'} strokeWidth="1.2" />
                );
              }))}

              {/* Stencil labels */}
              {[
                { i: iC, j: jC, label: 'uᵢⱼ', col: '#c0392b' },
                { i: iC - 1, j: jC, label: 'uᵢ₋₁ⱼ', col: '#2E75B6' },
                { i: iC + 1, j: jC, label: 'uᵢ₊₁ⱼ', col: '#2E75B6' },
                { i: iC, j: jC - 1, label: 'uᵢⱼ₋₁', col: '#2E75B6' },
                { i: iC, j: jC + 1, label: 'uᵢⱼ₊₁', col: '#2E75B6' },
              ].map(({ i, j, label, col }) => (
                <text key={label} x={gridX + i * dx} y={gridY + j * dt - 8}
                  textAnchor="middle" fontSize="9" fill={col} fontWeight="700">{label}</text>
              ))}

              {/* Axis labels */}
              <text x={gridX + (nX - 1) * dx + 10} y={gridY + jC * dt + 4} fontSize="10" fill="#888">x →</text>
              <text x={gridX - 18} y={gridY + (nT - 1) * dt} fontSize="10" fill="#888" transform={`rotate(-90,${gridX - 18},${gridY + (nT - 1) * dt / 2})`}>t →</text>
              <text x={gridX + iC * dx + 16} y={gridY + jC * dt - 2} fontSize="8.5" fill="#888">Δx</text>
              <text x={gridX + iC * dx - 18} y={gridY + jC * dt + 22} fontSize="8.5" fill="#888">Δt</text>

              {/* Equations */}
              <rect x={14} y={240} width={PW - 20} height={64} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1.5" />
              <text x={20} y={256} fontSize="10.5" fill="#7B0D0D" fontWeight="700">Wave equation: ∂²u/∂t² = c²·∂²u/∂x²</text>
              <text x={20} y={272} fontSize="10" fill="#c0392b" fontFamily="'Courier New',monospace">(uᵢⱼ₊₁ − 2uᵢⱼ + uᵢⱼ₋₁)/Δt² = c²(uᵢ₊₁ⱼ − 2uᵢⱼ + uᵢ₋₁ⱼ)/Δx²</text>
              <text x={20} y={288} fontSize="9.5" fill="#333">→ uᵢⱼ₊₁ = r²uᵢ₊₁ⱼ + 2(1−r²)uᵢⱼ + r²uᵢ₋₁ⱼ − uᵢⱼ₋₁  (r = cΔt/Δx)</text>
              <text x={20} y={302} fontSize="9.5" fill="#555">CFL stability condition: r = cΔt/Δx ≤ 1  (Courant-Friedrichs-Lewy)</text>
            </g>
          );
        })()}

        {/* Panel 2: FD derivative formulas + method of lines */}
        <rect x={PW + 20} y={10} width={PW} height={H - 20} rx={5} fill="#fff" stroke="#f0cece" />
        <text x={PW + 20 + PW / 2} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Finite Difference Formulas and Method of Lines</text>

        {[
          { y: 40, col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
            title: 'First Derivatives',
            lines: [
              'Forward:  u\'ᵢ = (uᵢ₊₁ − uᵢ)/Δx + O(Δx)',
              'Backward: u\'ᵢ = (uᵢ − uᵢ₋₁)/Δx + O(Δx)',
              'Central:  u\'ᵢ = (uᵢ₊₁ − uᵢ₋₁)/(2Δx) + O(Δx²)',
            ] },
          { y: 120, col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
            title: 'Second Derivative (Central)',
            lines: [
              'u\'\'ᵢ = (uᵢ₊₁ − 2uᵢ + uᵢ₋₁)/Δx² + O(Δx²)',
              'Stencil: weights [1, −2, 1]/Δx²',
              '4th-order: [−1,16,−30,16,−1]/(12Δx²)',
            ] },
          { y: 200, col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
            title: 'Method of Lines (MOL)',
            lines: [
              'Discretise space → ODE system in time',
              'du/dt = A·u  (A = spatial FD matrix)',
              'Apply any ODE integrator (RK4, BDF) to time',
              'Eigenvalues of A → stability requirement on Δt',
              'Spectral methods: Fourier/Chebyshev for better accuracy',
            ] },
        ].map(({ y, col, bg, bc, title, lines }) => (
          <g key={y}>
            <rect x={PW + 26} y={y} width={PW - 14} height={lines.length * 18 + 20} rx={3} fill={bg} stroke={bc} strokeWidth="1.2" />
            <text x={PW + 32} y={y + 15} fontSize="11" fill={col} fontWeight="700">{title}</text>
            {lines.map((line, li) => (
              <text key={li} x={PW + 32} y={y + 30 + li * 18} fontSize="9.5" fill="#333" fontFamily="'Courier New',monospace">{line}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Finite differences replace continuous derivatives with algebraic approximations on a discrete space-time grid. The central difference second derivative (uᵢ₊₁ − 2uᵢ + uᵢ₋₁)/Δx² is second-order accurate with the compact three-point stencil shown. For the wave equation, stability requires the Courant-Friedrichs-Lewy (CFL) condition r = cΔt/Δx ≤ 1: the numerical wavespeed must not exceed the physical wavespeed. The method of lines discretises only the spatial derivatives to convert a PDE into a large system of ODEs, which any standard ODE integrator can then advance in time — separating spatial accuracy from temporal accuracy concerns.</div>
    </div>
  );
}

// ── Diagram 9: Comparison Table and Software Ecosystem ────────────
function DiagramMethodComparison() {
  const W = 900, H = 270;

  const methods = [
    {
      name: 'Euler (RK1)',
      col: '#c0392b', bg: '#FEF0F0', bc: '#c0392b',
      props: [['Order', '1'], ['Stages/step', '1'], ['Stability', 'Small region'], ['Cost/step', '★☆☆☆'], ['Best for', 'Teaching only']],
    },
    {
      name: 'RK4 (Classic)',
      col: '#2E75B6', bg: '#eaf3ff', bc: '#2E75B6',
      props: [['Order', '4'], ['Stages/step', '4'], ['Stability', 'Moderate region'], ['Cost/step', '★★☆☆'], ['Best for', 'Smooth non-stiff ODEs']],
    },
    {
      name: 'RK45 (Adaptive)',
      col: '#27ae60', bg: '#f0fff4', bc: '#27ae60',
      props: [['Order', '4/5'], ['Stages/step', '6'], ['Stability', 'Moderate adaptive'], ['Cost/step', '★★★☆'], ['Best for', 'General ODEs (default)']],
    },
    {
      name: 'Backward Euler',
      col: '#e07b00', bg: '#fff9e6', bc: '#e07b00',
      props: [['Order', '1'], ['Stages/step', '1+Newton'], ['Stability', 'Entire LHP (A-stable)'], ['Cost/step', '★★★☆'], ['Best for', 'Stiff problems']],
    },
    {
      name: 'Newmark-β',
      col: '#9b59b6', bg: '#f8f0ff', bc: '#9b59b6',
      props: [['Order', '2'], ['Stages/step', '1 linear solve'], ['Stability', 'Uncond. (β≥γ/2)'], ['Cost/step', '★★★☆'], ['Best for', 'Structural dynamics']],
    },
    {
      name: 'BDF (stiff)',
      col: '#7B0D0D', bg: '#fff5f5', bc: '#c0392b',
      props: [['Order', '1–6'], ['Stages/step', 'Multi-step'], ['Stability', 'Stiffly stable'], ['Cost/step', '★★★★'], ['Best for', 'Very stiff / DAEs']],
    },
  ];

  const cardW = (W - 20) / 3, cardH = 120;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Numerical Method Comparison — Selection Guide</div>
      <svg viewBox={`0 0 ${W} 258`} style={{ width: '100%', display: 'block' }}>
        {methods.map(({ name, col, bg, bc, props }, i) => {
          const c = i % 3, r = Math.floor(i / 3);
          const x = 8 + c * (cardW + 3), y = 8 + r * (cardH + 6);
          return (
            <g key={i}>
              <rect x={x} y={y} width={cardW} height={cardH} rx={5} fill={bg} stroke={bc} strokeWidth="1.5" />
              <rect x={x} y={y} width={cardW} height={24} rx={4} fill={bc} />
              <text x={x + cardW / 2} y={y + 16} textAnchor="middle" fontSize="11.5" fill="#fff" fontWeight="700">{name}</text>
              {props.map(([key, val], pi) => (
                <g key={pi}>
                  <rect x={x + 4} y={y + 28 + pi * 18} width={cardW - 8} height={17} rx={2}
                    fill={pi % 2 === 0 ? 'rgba(255,255,255,0.5)' : 'transparent'} />
                  <text x={x + 8} y={y + 40 + pi * 18} fontSize="9.5" fill={col} fontWeight="700">{key}:</text>
                  <text x={x + cardW - 8} y={y + 40 + pi * 18} textAnchor="end" fontSize="9.5" fill="#333">{val}</text>
                </g>
              ))}
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>Method selection depends on the problem type. For smooth, non-stiff ODEs (free vibration, simple nonlinear oscillators), RK45 with adaptive step size is the default choice — it balances accuracy and efficiency automatically. When the system has widely-separated time scales (stiff: structural dynamics with rigid members, chemical kinetics), backward Euler or BDF methods with implicit solvers are essential to avoid prohibitively small step sizes. For large structural dynamics models in FEA, Newmark-β or HHT-α are preferred because they exploit the special structure of second-order systems (M, C, K matrices) and allow unconditional stability with only one matrix factorisation per step.</div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────
export default function NumericalMethods() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Numerical Methods in System Dynamics</h1>
      <p style={S.p}>
        System dynamics problems are almost always governed by differential equations that have no closed-form analytical solution. Numerical methods transform these continuous equations into discrete algebraic problems that computers can solve step by step. The choice of method — explicit or implicit, fixed or adaptive step size, first-order or higher-order — profoundly affects the accuracy, stability, and computational cost of the simulation. Understanding the mathematical foundations of these methods is essential for any engineer who uses simulation tools: knowing why a solver fails, how to interpret its results, and how to choose appropriate tolerances and step sizes separates reliable simulation from blind button-pressing.
      </p>

      <InfoBox title="Core Ideas at a Glance" items={[
        "ODE integration: discretise y' = f(t,y) into time steps; accuracy measured by global error vs. step size dt",
        "Order p: global error scales as O(dt^p) — RK4 (p=4) is 16× more accurate than Euler (p=1) for the same dt",
        "Stability region: set of h·λ values for which numerical solution stays bounded — must contain all problem eigenvalues",
        "Stiffness: eigenvalue spread |λ_max|/|λ_min| >> 1 — explicit methods require tiny steps; implicit methods do not",
        "Newmark-β (β=1/4, γ=1/2): unconditionally stable, 2nd-order, no numerical damping — standard for structural FEA",
        "FFT computes DFT in O(N log₂ N) instead of O(N²) — enables frequency analysis of long time records",
        "CFL condition: cΔt/Δx ≤ 1 for wave equation — numerical wavespeed must not exceed physical wavespeed",
        "Adaptive step control: compare p-th and (p+1)-th order embedded solutions; shrink/grow step to meet tolerance",
        "Richardson extrapolation: run at h and h/2; combine to eliminate leading error — improves order by 1",
        "FEM assembly: local element matrices stacked into global banded [K] and [M]; eigenvalue problem gives modes",
      ]} />

      <h2 style={S.h2}>Euler vs. Runge-Kutta — Accuracy Visualised</h2>
      <p style={S.p}>
        The most fundamental question in numerical integration is: how does error grow with step size? First-order Euler accumulates error proportional to dt; fourth-order RK4 accumulates error proportional to dt⁴. The consequence is dramatic: halving the step size reduces Euler error by 2× but reduces RK4 error by 16×.
      </p>
      <DiagramEulerRK4 />
      <Table2 rows={[
        ["Euler method", "y_{n+1} = yₙ + dt·f(tₙ, yₙ)\nLocal truncation error (LTE): O(dt²) per step\nGlobal error: O(dt) — first-order method\nSimple but requires very small dt for accuracy"],
        ["Midpoint (RK2)", "k₁ = f(tₙ, yₙ);  k₂ = f(tₙ+dt/2, yₙ+dt/2·k₁)\ny_{n+1} = yₙ + dt·k₂\nLocal error O(dt³);  global error O(dt²)\nTwice the accuracy of Euler for same step count"],
        ["Classic RK4", "k₁=f(tₙ,yₙ);  k₂=f(tₙ+dt/2, yₙ+dt/2·k₁)\nk₃=f(tₙ+dt/2, yₙ+dt/2·k₂);  k₄=f(tₙ+dt, yₙ+dt·k₃)\ny_{n+1} = yₙ + (dt/6)(k₁+2k₂+2k₃+k₄)\nGlobal error O(dt⁴);  4 evaluations per step"],
        ["Local vs. global error", "Local: error introduced in one step (proportional to dt^{p+1})\nGlobal: accumulated over all steps to reach fixed time T\nGlobal = T/dt × local → global error = O(dt^p)\nGlobal order = local order − 1"],
        ["Efficiency comparison", "To achieve error 10⁻⁶: Euler needs dt ≈ 10⁻⁶;  RK4 needs dt ≈ 0.032\nRK4 uses ~10⁻⁶/0.032 × 4 = 1.25×10⁻⁴ the work of Euler\nRule: prefer highest-order method with smooth solutions"],
        ["Step size selection", "dt ≤ (time scale of fastest oscillation) / 20   (rough rule)\nFor undamped oscillator: dt ≤ T_n / 20 = 1/(20fₙ)\nAdaptive: solver chooses dt automatically to meet tolerance"],
      ]} />

      <h2 style={S.h2}>Runge-Kutta Family and Adaptive Integration</h2>
      <p style={S.p}>
        The Runge-Kutta family is systematically derived from Taylor series expansion. The Butcher tableau provides a compact notation that encodes the number of stages, their time offsets (c), their coupling coefficients (A), and the final weighted sum (b). Adaptive methods exploit embedded pairs of different orders to estimate local error and control step size automatically.
      </p>
      <DiagramRKFamily />
      <Table2 rows={[
        ["Butcher tableau notation", "c | A\n  | -----\n  | b\nc = stage time offsets; A = inter-stage weights (strictly lower triangular for explicit)\nb = final quadrature weights; consistency: Σbᵢ = 1"],
        ["Explicit vs. implicit RK", "Explicit: A is strictly lower triangular → each stage uses only previous stages\nImplicit: A has diagonal/upper entries → each stage depends on itself (solve nonlinear)\nDIRK: Diagonally Implicit — cheaper than fully implicit (A is lower triangular)"],
        ["Dormand-Prince RK45", "7 stages: FSAL (First Same As Last) — k₇ of step n = k₁ of step n+1\nEmbeds orders 4 and 5; error = ||y₅ − y₄|| / (atol + rtol·|y₄|)\nDefault: MATLAB ode45, Python scipy.integrate.solve_ivp, Julia DifferentialEquations.jl"],
        ["Step rejection and control", "Accept step if error ≤ 1;  reject and repeat with smaller h if error > 1\nStep factor: h_new = h_old · 0.9 · (1/error)^{1/5}\nMinimum factor: 0.1 (prevent catastrophically small steps)"],
        ["Runge-Kutta-Nyström (RKN)", "Specialised for second-order systems y'' = f(t,y)\nExploits structure: avoids converting to first-order system\nRequires fewer evaluations than applying standard RK to 2n-dim system\nUsed in orbit mechanics, structural dynamics"],
        ["Symplectic integrators", "Preserve the Hamiltonian structure of conservative systems\nLeapfrog/Störmer-Verlet: x_{n+1}=xₙ+dt·vₙ₊₁/₂; v_{n+1/2}=vₙ₊dt/2·aₙ\nNo numerical energy drift — essential for long-time dynamics\nUsed: molecular dynamics, celestial mechanics, nonlinear vibration"],
      ]} />

      <h2 style={S.h2}>Stability Regions and Stiff Systems</h2>
      <p style={S.p}>
        Numerical stability is separate from accuracy: a method can give the right answer when it converges, but diverge if the step size is too large. The stability region in the complex hλ plane must contain all eigenvalues of the problem multiplied by the step size. Stiff systems with widely-separated eigenvalues force explicit methods to use very small steps for stability, not accuracy.
      </p>
      <DiagramStabilityRegions />
      <Table2 rows={[
        ["Stability polynomial R(z)", "y_{n+1} = R(z)·yₙ for scalar test equation y' = λy\nR(z) = 1 + z + z²/2 + z³/6 + z⁴/24  (RK4 truncation of e^z)\nStable if |R(hλ)| ≤ 1 for all eigenvalues λ of system"],
        ["Stiffness ratio", "s = |λ_max|/|λ_min| = ratio of fastest to slowest time scales\nFor structural model: s = ωₙ_max/ωₙ_min (frequency ratio)\nFor chemical kinetics: s can reach 10⁶ or more\nExplicit RK: h ≤ 2.8/|λ_max| → extremely small steps for large s"],
        ["A-stability", "Stability region contains entire left half-plane (Re(z) ≤ 0)\nAll implicit methods with A-stable → no step size restriction from stability\nBackward Euler: A-stable, 1st order\nTrapezoidal rule: A-stable, 2nd order"],
        ["L-stability", "A-stable + R(z) → 0 as z → −∞\nL-stable methods damp stiff modes to zero in one step\nBackward Euler is L-stable; trapezoidal is not (oscillates)\nImportant for chemically reacting systems and DAEs"],
        ["Imaginary axis constraint", "Explicit RK4 stable on imaginary axis up to |z| = 2.83\nFor undamped oscillator: h·ωₙ ≤ 2.83 → h ≤ 2.83/(2πfₙ)\nExample: fₙ = 1000 Hz → h ≤ 0.00045 s  (2200 steps per cycle)"],
        ["Backward differentiation formulas (BDF)", "Multistep implicit methods: y_{n+k} + Σαⱼy_{n+j} = hβ_k f(t_{n+k}, y_{n+k})\nBDF1–BDF6 are stiffly stable — LHP plus a region near imaginary axis excluded\nBDF2 (backward difference): widely used balance of stability and accuracy\node15s (MATLAB), VODE, CVODE (Sundials) use BDF for stiff ODEs"],
      ]} />

      <h2 style={S.h2}>Newmark-β Method for Structural Dynamics</h2>
      <p style={S.p}>
        Structural dynamics problems Mü + Cu̇ + Ku = F(t) have special structure — second-order in time, with symmetric positive-definite M and K. The Newmark-β method exploits this structure via a predictor-corrector approach that requires only a single matrix factorisation per step (for linear systems), making it far more efficient than applying a general ODE solver to the equivalent first-order system.
      </p>
      <DiagramNewmark />
      <Table2 rows={[
        ["Newmark algorithm", "Given uₙ, u̇ₙ, üₙ at step n:\n1. Predict: ũ_{n+1} = uₙ + h·u̇ₙ + h²(0.5−β)·üₙ\n2. u̇̃_{n+1} = u̇ₙ + h(1−γ)·üₙ\n3. Solve: K_eff·u_{n+1} = F_{n+1} − C·u̇̃ − M/βh² · (ũ − u_{n+1})\n4. Correct: u̇_{n+1} = u̇̃ + γh·ü_{n+1};  u_{n+1} = ũ + βh²·ü_{n+1}"],
        ["Effective stiffness matrix", "K_eff = K + γ/(βh)·C + 1/(βh²)·M\nFactored once per linear step (LU or Cholesky)\nFor nonlinear: K_eff changes → re-factor each step (expensive)\nAverage accel. (β=0.25): K_eff = K + 2/h·C + 4/h²·M"],
        ["Stability for linear systems", "Unconditionally stable: β ≥ γ/2 ≥ 1/4\nConditionally stable (central difference, β=0): h ≤ 2/ωₙ_max\nAverage acceleration (β=1/4, γ=1/2): unconditional, no numerical damping\nNewmark: numerical damping iff γ > 1/2 (first-order amplitude error introduced)"],
        ["Accuracy", "Second-order accurate for displacement and velocity (O(h²))\nWhen γ = 1/2: no numerical damping, no phase error at low frequencies\nWhen γ > 1/2: numerical damping removes high-freq spurious modes\nHHT-α: modifies load vector interpolation to damp without affecting accuracy"],
        ["HHT-α (Hilber-Hughes-Taylor)", "Solves: M·ü_{n+1} + C·[(1+α)u̇_{n+1} − α·u̇ₙ] + K·[(1+α)u_{n+1} − α·uₙ] = F_{n+α+1}\nFor α ∈ [−1/3, 0]: unconditional stability + controllable numerical damping\nα = 0: standard Newmark (no damping);  α = −1/3: maximum high-freq damping\nUsed in Abaqus, Nastran, Ansys as the default dynamic integrator"],
        ["Wilson-θ method", "Extrapolates beyond step: evaluates forces at t + θ·h (θ ≥ 1.37 for stability)\nUnconditionally stable for θ ≥ 1.37, but introduces amplitude distortion\nLess popular than Newmark/HHT due to larger period elongation error\nθ = 1.4 often used in practice"],
      ]} />

      <h2 style={S.h2}>Finite Element Method for Vibration Analysis</h2>
      <p style={S.p}>
        The finite element method is the dominant approach for computing natural frequencies, mode shapes, and dynamic responses of complex structures. It converts the continuous elasticity equations into a finite-dimensional algebraic eigenvalue problem by discretising the structure into elements and approximating displacements within each element using polynomial shape functions.
      </p>
      <DiagramFEM />
      <Table2 rows={[
        ["Shape functions", "Nᵢ(x): polynomial basis that interpolates displacement from nodal values\nu(x) = Σᵢ Nᵢ(x)·uᵢ  within element\nLinear elements: Nᵢ linear between nodes — 1st-order accuracy\nQuadratic (Serendipity): midside nodes — 2nd-order, better for curved geometry"],
        ["Element stiffness matrix", "kₑ = ∫ BᵀEB dV  (B = strain-displacement; E = elasticity tensor)\nComputed by Gauss quadrature: k = Σⱼ wⱼ·Bᵀ(ξⱼ)·E·B(ξⱼ)·|J(ξⱼ)|\nSymmetric positive semi-definite;  size: (n_dof_per_elem)²"],
        ["Element mass matrices", "Consistent mass: mₑ = ρ∫ NᵀN dV\nLumped mass: diagonal mₑ = ρ·V_elem·I / n_nodes  (simpler, less accurate)\nConsistent mass: better accuracy, especially for higher modes\nLumped: faster assembly, diagonal M → easier eigenvalue solve"],
        ["Assembly process", "Global DOF numbering: each element node maps to global DOF index\nScatter: add element kₑ/mₑ entries into global K/M at mapped positions\nResult: banded sparse [K] and [M] — bandwidth = max DOF span per element\nBandwidth determines factorisation cost: O(n·b²) for bandwidth b"],
        ["Eigenvalue solvers", "Full problem: QR algorithm O(n³) — only for small n (<1000)\nLanczos: extracts k smallest eigenvalues in O(n·k·q) — q = Lanczos vectors\nSubspace iteration: simultaneous Rayleigh-Ritz for first k modes\nFEAST: contour-integral based, high accuracy, parallel-friendly"],
        ["Model reduction", "Craig-Bampton: keep physical boundary DOF + modal interior DOF\nGuyan reduction: condense interior DOF statically (loses accuracy at high ω)\nComponent mode synthesis: assemble reduced-order substructures\nPOD/SVD: data-driven reduction from simulation snapshots"],
      ]} />

      <h2 style={S.h2}>Error Analysis and Convergence</h2>
      <p style={S.p}>
        Verification of numerical results requires systematic convergence studies — checking that refining the mesh or reducing the time step converges to a fixed answer. Richardson extrapolation and embedded error estimators provide quantitative error bounds without requiring the exact solution.
      </p>
      <DiagramErrorAnalysis />
      <Table2 rows={[
        ["Convergence study", "Run simulation at h, h/2, h/4, ...\nPlot error vs. h on log-log: slope = order p\nIf no exact solution: use Richardson extrapolation as reference\nConverged when reducing h further changes result by less than tolerance"],
        ["Richardson extrapolation", "y_exact ≈ y_h + (y_{h/2} − y_h)/(2^p − 1)\nFor p=4: y_exact ≈ y_{h/2} + (y_{h/2} − y_h)/15\nError estimate: |e| ≈ |y_{h/2} − y_h|/(2^p − 1)\nVerification without exact solution — standard in CFD/FEA"],
        ["Global vs. local error", "Local truncation error (LTE): error in one step = O(h^{p+1})\nGlobal error: accumulated LTE over T/h steps = O(h^p)\nError propagation: global error grows if method not zero-stable\nZero-stability: method stable as h → 0 with fixed T"],
        ["Round-off error floor", "Machine epsilon: ε_mach ≈ 10⁻¹⁶ (double precision)\nAccumulated round-off: O(n · ε_mach) for n steps\nOptimal step: h_opt where truncation error = round-off error\nh_opt ≈ (ε_mach)^{1/(p+1)} — usually much smaller than needed for accuracy"],
        ["Verification vs. validation", "Verification: numerical method solving the equations correctly?\n  → Convergence tests, comparison with manufactured solutions (MMS)\nValidation: correct equations for the physical problem?\n  → Comparison with experiment, sensitivity analysis"],
        ["Manufactured solutions", "Choose any function u_exact(x,t); substitute into PDE\nCompute forcing term f = Lu_exact (residual source term)\nSolve numerically with this forcing; compare to u_exact\nRigorous verification method — works for any PDE/ODE"],
      ]} />

      <h2 style={S.h2}>FFT and Frequency-Domain Analysis</h2>
      <p style={S.p}>
        The Fast Fourier Transform is the cornerstone of computational spectral analysis. It converts time-domain signals to frequency-domain representations in O(N log N) operations, enabling natural frequency identification, frequency response function estimation, and power spectral density computation from measured or simulated data.
      </p>
      <DiagramFFT />
      <Table2 rows={[
        ["DFT definition", "X[k] = Σ_{n=0}^{N-1} x[n]·e^{−j2πkn/N},  k = 0,...,N−1\nInverse: x[n] = (1/N)Σ X[k]·e^{j2πkn/N}\nFrequency bins: f_k = k·fs/N;  frequency resolution Δf = fs/N"],
        ["FFT algorithm", "Cooley-Tukey: recursively splits N-point DFT into two N/2-point DFTs\nComplexity: O(N log₂ N) vs. O(N²) for DFT — 1000× faster for N=1024\nRequires N = 2^m (use zero-padding to nearest power of 2)\nBluestein: arbitrary N; good-Thomas: N=p×q (coprime)"],
        ["Parseval's theorem", "Σ|x[n]|² = (1/N)Σ|X[k]|²  (energy in time = energy in frequency)\nPSD: S[k] = |X[k]|²/(N·fs) [unit²/Hz]\nσ² = Σ S[k]·Δf  (mean square from PSD)"],
        ["Spectral leakage", "Occurs when signal frequency not an exact multiple of Δf\nSide lobes of DFT window smear energy to adjacent bins\nWindow functions: Hann (−32 dB side lobes), flat-top (±0.01 dB amplitude), Kaiser\nTrade-off: wider main lobe for lower side lobes"],
        ["FRF from FFT", "H(f) = Y(f)/X(f)  — ratio of output to input spectra\nH₁ = S_XY/S_XX  (minimises output noise)\nCoherence: γ² = |S_XY|²/(S_XX·S_YY) — quality indicator\nAverage multiple records to improve coherence estimate"],
        ["Zero-padding", "Appending zeros before FFT does NOT add information\nIncreases spectral resolution (more bins) by interpolation\nUseful for: smooth spectrum display, interpolating peak location\nDoes not improve DFT frequency resolution — need longer actual record"],
      ]} />

      <h2 style={S.h2}>Finite Differences and PDEs</h2>
      <p style={S.p}>
        Finite differences extend the ODE integration concept to partial differential equations by discretising both space and time on a grid. The method of lines separates spatial and temporal discretisation, converting the PDE to a large ODE system that standard integrators can handle.
      </p>
      <DiagramFiniteDiff />
      <Table2 rows={[
        ["Finite difference approximations", "Central 2nd derivative: (uᵢ₊₁ − 2uᵢ + uᵢ₋₁)/Δx² + O(Δx²)\nHigher-order: (−uᵢ₊₂ + 16uᵢ₊₁ − 30uᵢ + 16uᵢ₋₁ − uᵢ₋₂)/(12Δx²) + O(Δx⁴)\nForward time: (u^{n+1} − uⁿ)/Δt + O(Δt);  central: (u^{n+1} − u^{n-1})/(2Δt) + O(Δt²)"],
        ["CFL condition", "For wave equation u_tt = c²u_xx:  CFL = c·Δt/Δx ≤ 1\nFor heat equation u_t = α u_xx:  r = αΔt/Δx² ≤ 0.5\nExplicit stable iff CFL ≤ 1;  implicit methods: CFL unrestricted\nCFL = 1: exact (characteristic speed = grid speed) for wave equation"],
        ["FTCS, Lax-Wendroff", "FTCS (forward time central space): explicit, 1st-order time, 2nd-order space\nUnstable for wave equation without dissipation!\nLax-Wendroff: 2nd-order both time and space; includes artificial diffusion\nUpwind: 1st-order, stable for convection, highly diffusive"],
        ["Method of lines", "Discretise space only → ODE: du/dt = A·u  (A = spatial matrix)\nChoose any ODE integrator for time stepping\nCoupling: spatial eigenvalues of A determine stability constraint on Δt\nSpectral methods: replace FD with global basis functions (Fourier, Chebyshev)"],
        ["Sparse linear algebra", "FD and FEM produce large sparse matrices (millions of DOF)\nDirect: sparse LU (PARDISO, UMFPACK) — direct factorisation O(n^{3/2})\nIterative: Conjugate Gradient (symmetric positive definite), GMRES (general)\nPreconditioner: ILU, AMG (algebraic multigrid) — accelerates convergence\nFor eigenproblems: ARPACK, SLEPc (shift-invert Lanczos)"],
        ["Spectral element method", "High-order polynomial basis functions on each element (p-refinement)\nExponential convergence for smooth solutions vs. algebraic for FD/FEM\nUsed in: global wave propagation (seismology), fluid dynamics (turbulence)\nCombines h-refinement (more elements) with p-refinement (higher order)"],
      ]} />

      <h2 style={S.h2}>Method Selection Guide</h2>
      <p style={S.p}>
        No single numerical method is optimal for all problems. Understanding the characteristics of each method — order, stability region, cost per step, and special requirements — allows the engineer to match the solver to the problem and to diagnose failures when they occur.
      </p>
      <DiagramMethodComparison />
      <Table2 rows={[
        ["Smooth, non-stiff ODEs", "RK45 (Dormand-Prince) with adaptive step control\nDefault in most scientific computing environments\nRTol ≈ 1e-3 to 1e-6 depending on accuracy needs\nExamples: single-DOF vibration, pendulum, trajectory problems"],
        ["Stiff ODEs (λ range large)", "Backward Euler (simple, robust) or BDF2–BDF5 (accurate)\nTrap rule (Crank-Nicolson) for second-order accuracy and A-stability\node15s (MATLAB), solve_ivp(method='BDF') (Python)\nExamples: structural with rigid constraints, chemical kinetics, power systems"],
        ["Large structural FEA", "Newmark-β (β=1/4, γ=1/2) or HHT-α for second-order systems\nExplicit central difference only for very short events (shock, blast)\nModal superposition if only first few modes matter (>> 10× cheaper)\nExamples: earthquake response, impact, acoustic fatigue"],
        ["Natural frequencies/modes", "Lanczos or subspace iteration for first k modes of large sparse system\nFull QR for dense systems (n < 1000)\nShift-invert: find modes near a frequency σ by solving (K−σM)v = Mv\nAMLS (Automated Multilevel Substructuring): extreme scale (millions of DOF)"],
        ["Long-time integration", "Symplectic integrators (Verlet, Ruth-Forest) preserve Hamiltonian structure\nNo energy drift over millions of cycles — critical for orbital mechanics\nGeometric integration: methods that preserve manifold structure of solution\nExamples: molecular dynamics, nonlinear vibration stability analysis"],
        ["Real-time simulation", "Must complete one integration step in less than Δt wall-clock time\nExplicit Euler or RK4 with fixed step (no Newton solve)\nHardware-in-the-loop: xPC Target, dSPACE, Speedgoat\nStep constraint: dt ≥ τ_hardware_latency (typically 0.1–1 ms)"],
      ]} />

      <InfoBox title="Key Formulae Summary" items={[
        "Euler: y_{n+1} = yₙ + dt·f(tₙ,yₙ);  error O(dt);  RK4: error O(dt⁴)",
        "RK4: y_{n+1} = yₙ + (dt/6)(k₁+2k₂+2k₃+k₄) where kᵢ = f(tᵢ, yᵢ)",
        "Stability: method stable iff |R(hλ)| ≤ 1 for all eigenvalues λ of system",
        "Newmark: K_eff = K + γ/(βh)C + 1/(βh²)M;  unconditional for β ≥ γ/2",
        "FEM eigenvalue: [K − ωₙ²M]{φ} = {0};  global K = assembly of kₑ = ∫BᵀEB dV",
        "Richardson: y_exact ≈ y_{h/2} + (y_{h/2}−y_h)/(2^p−1);  eliminates O(h^p) term",
        "FFT: O(N log₂ N);  Δf = fs/N;  CFL: c·Δt/Δx ≤ 1 (wave equation stability)",
        "Adaptive step: h_new = h·0.9·(tol/err)^{1/(p+1)};  accept if err ≤ tolerance",
      ]} />
    </div>
  );
}